import { useQueryClient } from "@tanstack/react-query";
import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { setTokenGetter } from "../api/client";
import { purgePersistedClientState, readCacheOwner, writeCacheOwner } from "../state/persistedSession";
import { useUiStore } from "../state/uiStore";
import { supabase, supabaseConfigured } from "./supabase";

// SessionProvider — hält Identität + Access-Token und speist den API-Client (Bearer).
// „signed-out" ist ein erstklassiger Zustand (Dev ohne Supabase-Env, oder abgemeldet):
// die Shell rendert vollständig, datenabhängige Panels zeigen definierte Leerzustände.
//
// Cache-Hygiene (D47/D149, Codex-P0/P1): der persistierte Query-Cache (D149) + Delta-Cursor sind
// an EINEN `cacheOwner` (localStorage) gebunden. Bootstrap mit DERSELBEN Identität → NICHT leeren
// (der hydrierte Cache bleibt → Sofort-Paint). Identitätswechsel/Logout → in-memory-Cache,
// persistierter Cache+Cursor UND der user-gebundene uiStore-State (Drafts, Input-Previews mit
// presigned URLs, geöffneter Job, D65) werden gelöscht — kein Cross-User-Bleed im selben Browser.
// Der SessionProvider liegt INNERHALB des QueryClientProvider (App.tsx).

export type SessionStatus = "loading" | "signed-in" | "signed-out" | "unconfigured";

export interface SessionUser {
  id: string;
  email: string | null;
}

interface SessionValue {
  status: SessionStatus;
  user: SessionUser | null;
  token: string | null; // aktuelles Access-Token (für den Live-Status-Connect, D47)
  signIn: (email: string, password: string) => Promise<{ ok: boolean; message: string }>;
  signUp: (name: string, email: string, password: string) => Promise<{ ok: boolean; message: string }>;
  signOut: () => Promise<void>;
}

const Ctx = createContext<SessionValue | null>(null);

// Supabase-Auth-Fehler → knappe deutsche Meldung (Roh-Message nur als Fallback).
function authMessage(error: { message: string }): string {
  const m = error.message.toLowerCase();
  if (m.includes("invalid login credentials")) return "E-Mail oder Passwort ist falsch.";
  if (m.includes("email not confirmed")) return "Bitte bestätige zuerst deine E-Mail (Link in der Mailbox).";
  if (m.includes("already registered") || m.includes("already been registered"))
    return "Diese E-Mail ist bereits registriert — bitte anmelden.";
  if (m.includes("rate limit")) return "Zu viele Versuche — bitte kurz warten.";
  return error.message;
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState<SessionStatus>(supabaseConfigured ? "loading" : "unconfigured");
  const [user, setUser] = useState<SessionUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const tokenRef = useRef<string | null>(null);
  const lastUserId = useRef<string | null>(null);

  // API-Client liest das Token immer aus der Ref (kein Stale-Closure).
  useEffect(() => {
    setTokenGetter(() => tokenRef.current);
  }, []);

  useEffect(() => {
    if (!supabase) return;
    let active = true;

    // Eine Session anwenden: NUR bei echtem Identitätswechsel leeren (D149/Codex-P1). Die Quelle
    // der Wahrheit ist der PERSISTIERTE `cacheOwner` (überlebt Reload) — NICHT `lastUserId.current`
    // (startet bei jedem Mount null und hätte beim Bootstrap mit derselben Session den gerade
    // hydrierten persistierten Cache verworfen → kein Sofort-Paint). Bei fremdem/leerem Owner:
    // in-memory + persistierten Cache + Delta-Cursor purgen (kein Cross-User-Bleed, D47/Codex-P0).
    const apply = (s: { access_token: string; user: { id: string; email?: string | null } } | null) => {
      const newId = s?.user.id ?? null;
      if (readCacheOwner() !== newId) {
        qc.clear();
        purgePersistedClientState();
        useUiStore.getState().resetUserScopedState();
        writeCacheOwner(newId);
      }
      lastUserId.current = newId;
      tokenRef.current = s?.access_token ?? null;
      setToken(s?.access_token ?? null);
      setUser(s ? { id: s.user.id, email: s.user.email ?? null } : null);
      setStatus(s ? "signed-in" : "signed-out");
    };

    void supabase.auth.getSession().then(({ data }) => {
      if (active) apply(data.session);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => apply(s));
    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, [qc]);

  const value = useMemo<SessionValue>(
    () => ({
      status,
      user,
      token,
      async signIn(email: string, password: string) {
        if (!supabase) return { ok: false, message: "Supabase nicht konfiguriert (VITE_SUPABASE_URL/ANON_KEY)." };
        const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
        return error ? { ok: false, message: authMessage(error) } : { ok: true, message: "Angemeldet." };
      },
      async signUp(name: string, email: string, password: string) {
        if (!supabase) return { ok: false, message: "Supabase nicht konfiguriert (VITE_SUPABASE_URL/ANON_KEY)." };
        // `display_name` → user_metadata → Onboarding-Trigger `handle_new_user` (§9.2) füllt
        // die profiles-Zeile. E-Mail-Bestätigung bleibt an (Verify-Link); `emailRedirectTo`
        // führt den Link zurück auf die laufende Origin.
        const { data, error } = await supabase.auth.signUp({
          email: email.trim(),
          password,
          options: { data: { display_name: name.trim() }, emailRedirectTo: window.location.origin },
        });
        if (error) return { ok: false, message: authMessage(error) };
        return {
          ok: true,
          message: data.session
            ? "Konto erstellt — du bist angemeldet."
            : "Konto erstellt. Bestätige deine E-Mail über den Link, dann anmelden.",
        };
      },
      async signOut() {
        await supabase?.auth.signOut();
        tokenRef.current = null;
        lastUserId.current = null;
        qc.clear(); // abgemeldet → keine fremd-sichtbaren Reste (D47)
        purgePersistedClientState(); // D149: persistierten Cache + Delta-Cursor + Owner mit weg
        useUiStore.getState().resetUserScopedState(); // Drafts/Input-Previews/geöffneter Job (D65)
        setToken(null);
        setUser(null);
        setStatus(supabaseConfigured ? "signed-out" : "unconfigured");
      },
    }),
    [status, user, token, qc],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionValue {
  const v = useContext(Ctx);
  if (!v) throw new Error("useSession außerhalb von <SessionProvider>");
  return v;
}
